import { Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { Boss, type BossDef } from './boss-base';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * Bastion — a stationary fortress wrapped in a rotating shield arc with
 * a single GAP. Shots only land when they come in through the gap, so the
 * player must read the rotation and circle to the open side. Pure positioning
 * puzzle — no twitch needed, friendly for all ages. docs/02 boss pool.
 * Signatures: rotating shield gap (always), bolt ring (P1/P2).
 */
export const BASTION: BossDef = {
  id: 'bastion',
  nameKey: 'boss.bastion',
  tier: 3,
  minRoom: 2,
  baseHp: 1400,
  radius: 50,
  bodyKey: 'boss/bastion/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/bolt', count: 8, interval: 2.8,
          spinDeg: 0, speed: 115, radius: 10, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.5,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/bolt', count: 10, interval: 2.6,
          spinDeg: 18, speed: 125, radius: 10, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ring', count: 3, spreadDeg: 20,
          interval: 2.6, telegraph: 0.4, speed: 235, radius: 10, damage: 2,
          unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Counter-battery: a second bolt ring turning against the first
      tier: 2,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/bolt', count: 6, interval: 3.6,
        spinDeg: -20, phaseDeg: 30, speed: 105, radius: 10, damage: 1,
      },
    },
    {
      // Keep sniper: a telegraphed single-shot burst once the walls crack
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/bolt', count: 1, spreadDeg: 0,
        interval: 3.2, telegraph: 0.5, burst: 3, burstGap: 0.16,
        speed: 300, radius: 10, damage: 1,
      },
    },
  ],
};

const SHIELD_R = 66;
const GAP_HALF = 0.5; // radians — the open wedge half-width
const SPIN = 0.7; // shield rotation speed (rad/s)
const CLINK_FLASH = 0.08;

export class BastionBoss extends Boss {
  private gapAngle = 0;
  private readonly shield = new Graphics();
  private lastPx = 0;
  private lastPy = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(BASTION, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.view.addChild(this.shield);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.lastPx = px;
    this.lastPy = py;
    this.gapAngle += SPIN * this.levelSpeedMul * dt;
  }

  /** True when a point around the keep lines up with the open gap. */
  private inGap(x: number, y: number): boolean {
    let diff = Math.atan2(y - this.y, x - this.x) - this.gapAngle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return Math.abs(diff) <= GAP_HALF;
  }

  /**
   * Damage only lands through the gap — so the keep counts as vulnerable
   * while the player stands on the open side (auto-aim, damage numbers).
   */
  override get isVulnerable(): boolean {
    return super.isVulnerable && this.inGap(this.lastPx, this.lastPy);
  }

  /** Anchored keep — only a tiny idle bob, no orbit drift. */
  protected override move(dt: number, px: number, py: number): void {
    void px; void py;
    this.x += (this.homeX - this.x) * Math.min(1, 2 * dt);
    this.y += (this.homeY - this.y) * Math.min(1, 2 * dt);
  }

  /**
   * Shots only damage when they enter through the rotating gap. Each impact
   * reports its own result, so damage numbers follow the impact angle (not
   * the ship's side like `isVulnerable`).
   */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const before = this.hp;
    const hits = bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg, bx, by) => {
      if (this.inGap(bx, by)) return this.landHit(dmg); // through the gap
      this.hitFlash = CLINK_FLASH; // clink off the shield
      return 0;
    });
    return { hits, dealt: before - Math.max(0, this.hp) };
  }

  /** Explosions follow the same rule: only a blast on the open side hurts (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (!this.hitByAoe(x, y, radius)) return 0;
    if (!this.inGap(x, y)) {
      this.hitFlash = CLINK_FLASH;
      return 0;
    }
    return super.applyAoe(x, y, radius, damage);
  }

  override render(alpha: number): void {
    super.render(alpha);
    // Shield arc = full ring minus the gap wedge
    this.shield.clear();
    this.shield
      .arc(0, 0, SHIELD_R, this.gapAngle + GAP_HALF, this.gapAngle - GAP_HALF + Math.PI * 2)
      .stroke({ color: COLORS.uiAccent, width: 7, alpha: 0.85, cap: 'round' });
    this.shield
      .arc(0, 0, SHIELD_R, this.gapAngle + GAP_HALF, this.gapAngle - GAP_HALF + Math.PI * 2)
      .stroke({ color: 0xffffff, width: 2, alpha: 0.3 });
    void alpha;
  }
}
